import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Loader } from '@mantine/core';
import { auth } from '../api/authApi';

interface GoogleLoginButtonProps {
  onSuccess: (user: any, token: string) => void;
  onError: (error: string) => void;
  onAccountLinkingRequired: (userId: number, token: string) => void;
  disabled?: boolean;
  text?: string;
}

declare global {
  interface Window {
    google?: any;
    handleCredentialResponse?: (response: any) => void;
  }
}

export const GoogleLoginButton: React.FC<GoogleLoginButtonProps> = ({
  onSuccess,
  onError,
  onAccountLinkingRequired,
  disabled = false,
  text = 'Sign in with Google'
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [initializationError, setInitializationError] = useState<string | null>(null);
  const lastClickRef = useRef(0);
  const isInitializingRef = useRef(false);

  const initializeGoogle = useCallback(async () => {
    if (isInitializingRef.current) return;
    isInitializingRef.current = true;

    try {
      if (!window.google?.accounts?.id) {
        throw new Error('Google Identity Services not available');
      }

      window.handleCredentialResponse = handleCredentialResponse;
      
      window.google.accounts.id.initialize({
        client_id: '335201446481-qipac5o1v75tok8jtb011jlr8litrc47.apps.googleusercontent.com',
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
        use_fedcm_for_prompt: false,
      });
      
      setIsGoogleLoaded(true);
      setInitializationError(null);
    } catch (error) {
      const errorMsg = 'Failed to initialize Google login';
      console.error('Google initialization error:', error);
      setInitializationError(errorMsg);
      onError(errorMsg);
    } finally {
      isInitializingRef.current = false;
    }
  }, [onError]);

  useEffect(() => {
    const loadGoogleScript = async () => {
      // Check if script already exists
      if (document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
        if (window.google?.accounts?.id) {
          initializeGoogle();
        }
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        // Wait for Google to be fully loaded
        const checkGoogle = () => {
          if (window.google?.accounts?.id) {
            initializeGoogle();
          } else {
            setTimeout(checkGoogle, 100);
          }
        };
        checkGoogle();
      };
      
      script.onerror = () => {
        const errorMsg = 'Failed to load Google login. Please check your internet connection.';
        console.error('Failed to load Google Identity Services');
        setInitializationError(errorMsg);
        onError(errorMsg);
      };
      
      document.head.appendChild(script);
    };

    loadGoogleScript();

    return () => {
      delete window.handleCredentialResponse;
    };
  }, [initializeGoogle, onError]);

  const handleCredentialResponse = useCallback(async (response: any) => {
    if (isLoading) return; // Prevent multiple concurrent requests
    
    setIsLoading(true);
    try {
      const result = await auth.googleLogin(response.credential);
      
      if (result.success && result.authResponse) {
        onSuccess(result.authResponse.user, result.authResponse.token);
      } else if (result.requiresLinking && result.userId) {
        onAccountLinkingRequired(result.userId, response.credential);
      }
    } catch (error) {
      console.error('Google login error:', error);
      onError(error instanceof Error ? error.message : 'Google login failed');
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, onSuccess, onAccountLinkingRequired, onError]);

  const handleGoogleLogin = useCallback(() => {
    // Debouncing: Prevent clicks within 2 seconds of each other
    const now = Date.now();
    if (now - lastClickRef.current < 2000) {
      return;
    }
    lastClickRef.current = now;

    if (!window.google || !isGoogleLoaded) {
      onError('Google login is not ready yet. Please try again in a moment.');
      return;
    }

    if (isLoading) {
      return; // Already processing a login
    }

    try {
      // Use only the popup method for consistency
      setIsLoading(true);
      
      // Create a temporary container for the Google button
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.visibility = 'hidden';
      document.body.appendChild(tempContainer);

      window.google.accounts.id.renderButton(
        tempContainer,
        {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          width: 250
        }
      );

      // Find and click the rendered button to trigger popup
      setTimeout(() => {
        const googleButton = tempContainer.querySelector('div[role="button"]') as HTMLElement;
        if (googleButton) {
          googleButton.click();
        } else {
          console.error('Could not find Google button to click');
          onError('Failed to initialize Google login popup');
          setIsLoading(false);
        }
        // Clean up the temporary container
        document.body.removeChild(tempContainer);
      }, 100);

    } catch (error) {
      console.error('Google login trigger error:', error);
      onError('Failed to start Google login');
      setIsLoading(false);
    }
  }, [isGoogleLoaded, isLoading, onError]);

  const isButtonDisabled = disabled || isLoading || !isGoogleLoaded || !!initializationError;

  return (
    <button
      type="button"
      onClick={handleGoogleLogin}
      disabled={isButtonDisabled}
      className="google-login-btn"
      title={initializationError || (isGoogleLoaded ? undefined : 'Loading Google login...')}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        padding: '12px 16px',
        backgroundColor: '#fff',
        border: '1px solid #dadce0',
        borderRadius: '8px',
        color: initializationError ? '#dc2626' : '#3c4043',
        fontSize: '14px',
        fontWeight: '500',
        cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease',
        opacity: isButtonDisabled ? 0.6 : 1,
        boxShadow: '0 1px 2px 0 rgba(60, 64, 67, 0.30), 0 1px 3px 1px rgba(60, 64, 67, 0.15)',
      }}
      onMouseEnter={(e) => {
        if (!isButtonDisabled) {
          e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(60, 64, 67, 0.30), 0 2px 6px 2px rgba(60, 64, 67, 0.15)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(60, 64, 67, 0.30), 0 1px 3px 1px rgba(60, 64, 67, 0.15)';
      }}
    >
      {isLoading ? (
        <Loader type="bars" size="sm" color="#666" />
      ) : initializationError ? (
        'Google login failed'
      ) : !isGoogleLoaded ? (
        'Loading Google...'
      ) : (
        <>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            style={{ marginRight: '12px' }}
          >
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {text}
        </>
      )}
    </button>
  );
};